import { queryOptions, keepPreviousData } from '@tanstack/react-query'
import { apiClient } from '../client'
import type {
  PrdDraft,
  PrdDraftListResponse,
  PrdDraftStatus,
} from '../types'

export const prdDraftQueries = {
  list: (
    agentId: string,
    params?: {
      status?: PrdDraftStatus
      /**
       * Excludes a single status from the result. Drafts tab uses this with
       * `'published'` so published PRDs (which live in the Published tab)
       * don't leak in and short-page the pagination. Mutually exclusive
       * with `status` — server ignores excludeStatus when status is set.
       */
      excludeStatus?: PrdDraftStatus
      cursor?: string
      limit?: number
    },
  ) =>
    queryOptions({
      queryKey: [
        'prd-drafts',
        'list',
        agentId,
        params?.status ?? 'all',
        params?.excludeStatus ?? 'none',
        params?.cursor ?? null,
      ] as const,
      queryFn: async () => {
        const searchParams = new URLSearchParams()
        if (params?.status) searchParams.set('status', params.status)
        if (params?.excludeStatus && !params.status) {
          searchParams.set('excludeStatus', params.excludeStatus)
        }
        if (params?.cursor) searchParams.set('cursor', params.cursor)
        searchParams.set('limit', String(params?.limit ?? 20))
        return apiClient<PrdDraftListResponse>(
          `/api/v1/agents/${agentId}/prd-drafts?${searchParams.toString()}`,
        )
      },
      placeholderData: keepPreviousData,
    }),

  detail: (agentId: string, draftId: string) =>
    queryOptions({
      queryKey: ['prd-drafts', 'detail', agentId, draftId] as const,
      queryFn: () =>
        apiClient<PrdDraft>(
          `/api/v1/agents/${agentId}/prd-drafts/${draftId}`,
        ),
      staleTime: 1000 * 30,
    }),
}
